<template>
    <div class="container-fluid px-5 mt-4">

        <!-- Nadpis + akce -->
        <div class="d-flex align-items-center justify-content-between mb-4 detail-page-header">
            <div class="d-flex align-items-center gap-3">
                <button class="btn file-detail__back-btn" @click="$router.back()">
                    <img :src="iconChevronLeft" alt="">
                </button>
                <h1 class="credits-page__title mb-0">{{ $t('cert.certDetail') }}</h1>
                <span v-if="cert.name" class="cert-name-badge">{{ cert.name }}</span>
            </div>
            <div class="detail-page-header__actions">
                <button class="btn file-detail__delete-btn" @click="deleteCert()">
                    {{ $t('cert.deleteCert') }}
                    <span class="file-detail__delete-icon" :style="{ maskImage: `url(${iconTrash})`, webkitMaskImage: `url(${iconTrash})` }"></span>
                </button>
            </div>
        </div>

        <Skeletor v-if="loading" />

        <div v-if="!loading">

            <!-- Informace o certifikátu -->
            <div class="dashboard-section-header mb-3">
                <div class="d-flex align-items-center gap-2">
                    <img :src="iconFileInfo" alt="" class="dashboard-section-header__icon">
                    <span class="dashboard-section-header__title">{{ $t('cert.certInfo') }}</span>
                </div>
            </div>

            <div class="row g-3 mb-4">

                <!-- Karta: Název certifikátu -->
                <div class="col-12 col-md-4">
                    <div class="file-detail-card file-detail-card__body">
                        <div class="profile-data-field">
                            <span class="cert-list__label">{{ $t('cert.certName') }}</span>
                            <span class="cert-list__value">{{ cert.name }}</span>
                        </div>
                    </div>
                </div>

                <!-- Karta: Datum vydání -->
                <div class="col-12 col-md-4">
                    <div class="file-detail-card file-detail-card__body">
                        <div class="profile-data-field">
                            <span class="cert-list__label">{{ $t('cert.created') }}</span>
                            <span class="cert-list__value">{{ formatDateShort(cert.created_at) }}, {{ formatTime(cert.created_at) }}</span>
                        </div>
                    </div>
                </div>

                <!-- Karta: Soubor -->
                <div class="col-12 col-md-4">
                    <router-link
                        v-if="cert.file_uuid"
                        :to="{ name: 'FileDetail', params: { uuid: cert.file_uuid } }"
                        class="file-detail-card file-detail-card__body cert-info-file-card"
                    >
                        <div class="cert-info-file-icon">
                            <img :src="iconFile2" alt="" style="height:25px;">
                        </div>
                        <div class="profile-data-field flex-grow-1 cert-file-info-body">
                            <span class="cert-list__label">{{ $t('file.file') }}</span>
                            <span class="cert-list__value cert-filename-truncate">{{ cert.file_name ?? '—' }}</span>
                        </div>
                        <span class="cert-info-file-chevron flex-shrink-0">
                            <img :src="iconChevronRight" alt="" style="height:14px;">
                        </span>
                    </router-link>
                    <div v-else class="file-detail-card file-detail-card__body cert-info-file-card">
                        <div class="cert-info-file-icon">
                            <img :src="iconFile2" alt="" style="height:25px;">
                        </div>
                        <div class="profile-data-field flex-grow-1 min-w-0">
                            <span class="cert-list__label">{{ $t('file.file') }}</span>
                            <span class="cert-list__value">{{ cert.file_name ?? '—' }}</span>
                        </div>
                    </div>
                </div>

            </div>

            <!-- Blockchain TX link -->
            <div v-if="cert.blockchain_tx_hash" class="cert-detail__tx-row mb-5">
                <span class="cert-detail__tx-label">{{ $t('cert.blockchainTx') }}</span>
                <a :href="cert.blockchain_tx_url" target="_blank" rel="noopener" class="cert-detail__tx-link">
                    {{ cert.blockchain_tx_hash }}
                    <i class="bi bi-box-arrow-up-right cert-detail__tx-icon"></i>
                </a>
            </div>

            <!-- Sekce náhled certifikátu -->
            <div class="dashboard-section-header mb-3" :class="{ 'mt-5': !cert.blockchain_tx_hash }">
                <div class="d-flex align-items-center gap-2">
                    <img :src="iconFileInfo" alt="" class="dashboard-section-header__icon">
                    <span class="dashboard-section-header__title">{{ $t('cert.certPreview') }}</span>
                    <span class="file-ext-badge">PDF</span>
                </div>
            </div>

            <!-- Čeká na vygenerování -->
            <div v-if="!cert.generated_at" class="cert-detail__generating mb-5">
                <div class="spinner-border spinner-border-sm" role="status"></div>
                <span>{{ $t('cert.waitingUntilGenerated') }}</span>
            </div>

            <!-- PDF viewer + stáhnout -->
            <div v-if="cert.generated_at" class="cert-detail__pdf-wrap mb-5">
                <div class="cert-detail__pdf-inner">
                    <vue-pdf-embed v-if="pdfSrc" ref="pdfRef" :source="pdfSrc" />
                </div>
                <div class="d-flex justify-content-center mt-3">
                    <button class="btn file-detail__download-btn" @click="downloadCert()" :disabled="downloadingCert">
                        <span v-if="downloadingCert" class="spinner-border spinner-border-sm me-2" role="status"></span>
                        {{ $t('cert.certDownload') }}
                        <img v-if="!downloadingCert" :src="iconDownload" alt="" class="file-detail__download-icon">
                    </button>
                </div>
            </div>

        </div>

    </div>
</template>

<script>
import http from '@/http';
import { loadingState } from '@/loading.js';
import VuePdfEmbed from 'vue-pdf-embed';
import iconFile from '/public/img/new-design/icons/file.svg';
import iconSpecialOk from '/public/img/new-design/icons/special-ok-icon.svg';
import iconChevronLeft from '/public/img/new-design/icons/chevron-left.svg';
import iconFileInfo from '/public/img/new-design/icons/file_info.svg';
import iconFile2 from '/public/img/new-design/icons/file_2.svg';
import iconChevronRight from '/public/img/new-design/icons/chevron-right.svg';
import iconTrash from '/public/img/new-design/icons/trash.svg';
import iconDownload from '/public/img/new-design/icons/download.svg';

export default {
    components: { VuePdfEmbed },

    mounted() {
        loadingState.active = false;
        this.getCertDetail();
    },

    data() {
        return {
            cert: {},
            loading: true,
            pdfSrc: null,
            downloadingCert: false,
            iconFile,
            iconSpecialOk,
            iconChevronLeft,
            iconFileInfo,
            iconFile2,
            iconChevronRight,
            iconTrash,
            iconDownload,
        };
    },

    methods: {
        getCertDetail() {
            http.get('/certs/' + this.$route.params.uuid + '/detail').then((response) => {
                this.cert = response.data.cert;
                if (this.cert.pdf_base64) {
                    this.pdfSrc = 'data:application/pdf;base64,' + this.cert.pdf_base64;
                }
            }).catch(() => {}).finally(() => {
                this.loading = false;
            });
        },

        deleteCert() {
            this.$swal.fire({
                title: this.$t('cert.confirmDelete'),
                icon: 'warning',
                input: this.cert?.file_uuid ? 'checkbox' : false,
                inputValue: 1,
                inputPlaceholder: this.$t('cert.fileDeleteToo'),
                showCancelButton: true,
                confirmButtonColor: '#175cff',
                cancelButtonColor: '#d33',
                confirmButtonText: this.$t('general.yes'),
                cancelButtonText: this.$t('general.no'),
            }).then((result) => {
                if (result.isConfirmed) {
                    const deleteOnlyCert = !result.value;
                    http.post('/certs/' + this.$route.params.uuid + '/delete?deleteOnlyCert=' + deleteOnlyCert).then(() => {
                        this.$router.push({ name: 'CertList' });
                    });
                }
            });
        },

        downloadFile() {
            http.get('/files/' + this.cert.file_uuid + '/view?download=true', { responseType: 'blob' }).then((response) => {
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', this.cert.file_name);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            });
        },

        downloadCert() {
            this.downloadingCert = true;
            http.post('/certs/' + this.cert.uuid + '/download', { responseType: 'blob' }).then((response) => {
                const fileURL = `data:application/pdf;base64,${response.data}`;
                const link = document.createElement('a');
                link.href = fileURL;
                link.setAttribute('download', (this.cert.name || 'Certificate') + '.pdf');
                document.body.appendChild(link);
                link.click();
                link.remove();
            }).finally(() => {
                this.downloadingCert = false;
            });
        },

        formatDateShort(dateStr) {
            const d = new Date(dateStr);
            const days = ['Ne', 'Po', 'Út', 'St', 'Čt', 'Pá', 'So'];
            return `${days[d.getDay()]} ${d.getDate()}. ${d.getMonth() + 1}. ${d.getFullYear()}`;
        },

        formatTime(dateStr) {
            const d = new Date(dateStr);
            const pad = n => String(n).padStart(2, '0');
            return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
        },
    },
};
</script>

<style>
.cert-info-file-card {
    display: flex !important;
    flex-direction: row !important;
    align-items: center;
    gap: 16px;
    text-decoration: none;
    color: inherit;
    overflow: hidden;
    min-width: 0;
}
.cert-info-file-card:hover {
    background: #F0F0F0;
}

.cert-info-file-icon {
    width: 48px;
    height: 48px;
    background: #E2ECFB;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.cert-info-file-chevron {
    display: flex;
    align-items: center;
    justify-content: center;
    color: #101827;
    font-size: 16px;
    flex-shrink: 0;
    text-decoration: none;
}
.cert-info-file-chevron:hover {
    color: var(--bg-brand);
}

.cert-file-info-body {
    overflow: hidden;
    min-width: 0;
}

.cert-filename-truncate {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    width: 100%;
}

.cert-name-badge {
    display: inline-flex;
    align-items: center;
    height: 20px;
    padding: 0 8px;
    background: #E2ECFB;
    border: none;
    color: #000;
    font-size: 10px;
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 300px;
}
</style>